<template>
  <div
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <flashcard-header
      :answered="answered"
      :breadcrumbs="breadcrumbs"
      :category="currentFlashcardCategory"
      :flashcard="currentFlashcard"
      :show-progress="true"
    />
    <flashcard
      :answered="answered"
      :answer-ids="answerIds"
      :correct="correct"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      @shift-enter="answerFlashcard"
      @set-answer-ids="setAnswerIds"
      @set-raw-answer-data="setRawAnswerData"
    />
    <flashcard-footer
      :answered="answered"
      :answer-ids="answerIds"
      :category="currentFlashcardCategory"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      :show-bookmark="true"
      :show-flashcard-statuses="false"
      @tag="tagFlashcard"
      @answer="answerFlashcard"
    />
  </div>
</template>

<script>

import Flashcard from '@components/flashcards/flashcard';
import FlashcardFooter from '@components/flashcards/footer';
import FlashcardHeader from '@components/flashcards/header';

import CategoriesMixin from '@/mixins/categories';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import QuizMixin from '@/mixins/quizzes';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

export default {
  components: { Flashcard, FlashcardFooter, FlashcardHeader },
  mixins: [CategoriesMixin, FlashcardViewMixin, GlobalMixin, QuizMixin, QuestionsUIV2Mixin],
  computed: {
    breadcrumbs() {
      if (!this.currentFlashcardCategory) return [];
      let categories = this.currentFlashcardCategory.path.split('.').map(id => this.$store.getters['categories/categories'][id]);
      if (this.routePrefix === 'scenarios') {
        return categories.map(c => {
          return { name: c.name, route: { name: 'scenarios#category', params: { categoryTypeId: c.categoryTypeId, categoryId: c.id }, query: this.currentQuery } };
        });
      }
      let crumbs = categories.map(c => {
        return { name: c.name, route: {name: `${this.routePrefix}#index`, params: { categoryId: c.id}, query: this.currentQuery}};
      });
      return crumbs;
    },
    currentFlashcardCategory() {
      if (this.currentFlashcard.id) {
        let categoryId = this.$route.params.categoryId;
        return this.$store.getters['categories/categories'][categoryId];
      }
    },
    dashboard() {
      const lastDashboardIds = this.$store.getters['dashboards/lastVisitedId'];
      if (Array.isArray(lastDashboardIds) && lastDashboardIds.length > 0) {
        return this.$store.getters['dashboards/get'][lastDashboardIds[0]];
      }
      return this.$store.getters['dashboards/menuDefaultDashboard'];
    },
    nextFlashcardRoute() {
      if (this.nextFlashcard) {
        return {
          name: `${this.routePrefix}#flashcard`,
          params: { categoryId: this.$route.params.categoryId, flashcardId: this.nextFlashcard },
          query: this.currentQuery,
        };
      } else {
        return {
          name: `${this.routePrefix}#results`,
          params: { categoryId: this.$route.params.categoryId },
          query: this.currentQuery,
        };
      }
    },
    skipRationale() {
      // don't show rationales for standard quizzes and custom quizzes with reviewAfterFinish
      if (this.currentResource.destination) {
        return (this.currentResource.destination == 'custom_quiz' && this.currentFlashcardCategory.reviewAfterFinish) || this.currentResource.destination == 'quiz_categories';
      };
      // Assume that rationales should be skipped if no destination is present (in the case of deeplinked quizzes)
      return true;
    },
  },
  watch: {
    '$route.params.flashcardId': function(id) {
      if(!this.$store.getters['flashcards/flashcards'][id]) {
        let params = { ids: [id], rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }
    },
    'currentFlashcard.id': {
      immediate: true,
      handler: function(id) {
        this.resetFlashcard(id);
      },
    },
  },
  created() {
    let stateParams = ['flashcards', 'stats'];
    this.$store.dispatch('flashcardStates/fetchStates', stateParams);
    let params = {};

    params.categoryId = this.$route.params.categoryId;

    if (this.$route.params.flashcardId) {
      params.flashcardId = this.$route.params.flashcardId;
    }

    params.rateLimited = this.rateLimitingEnabled;

    this.sessionPromise = this.$store.dispatch('flashcards/createFlashcardSession', params);
  },
  mounted() {
    this.sessionPromise.then(flashcard => {
      if(flashcard) {
        const categoryId = this.$route.params.categoryId;
        const suffix= this.questionUiV2Enabled ? '-v2' : '';

        this.$router.replace({
          name: `${this.routePrefix}#flashcard${suffix}`,
          params: { categoryId, flashcardId: flashcard.id },
          query: this.currentQuery,
        });
      } else {
        this.$router.go(-1);
      }
    }).catch(e => {
      this.$router.go(-1);
      Promise.reject(e);
    });
  },
  methods: {
    answerFlashcard() {
      if (this.timer) {
        clearInterval(this.timer);
      }

      // Load next flashcard
      if (!this.$store.getters['flashcards/flashcards'][this.nextFlashcard] && this.nextFlashcard) {
        let params = { ids: this.nextFlashcardGroup, rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }

      // Evaluate the answer
      const correct = this.evaluateAnswer(this.currentFlashcard, this.answerIds, this.rawAnswerData);
      this.correct = correct;
      const isQOTD = this.$route.path.includes('/qotd');
      this.$analytics.events.answeredFlashcard(this.$store, this.currentFlashcardCategory, this.currentFlashcard, this.answerIds, this.rawAnswerData, correct, isQOTD, this.dashboard?.name);

      this.createCategoryState();

      let params = {
        timeInSeconds: this.time,
        rateLimited: this.rateLimitingEnabled,
      };

      this.createFlashcardState(params);
      this.decrementRateLimitCredits(this.currentFlashcard, this.currentFlashcardCategory);

      // If not showing rationale, just go to next question
      if (this.skipRationale) {
        this.goToNextFlashcard();
      } else {
        this.answered = true;
      }
    },
    createCategoryState() {
      const params = {
        number: this.quizFlashcardStates(this.$store.getters['categories/categories'][this.$route.params.categoryId]).length + 1,
        categoryStatusId: this.nextFlashcard ? this.inProgressStatus.id : this.completedStatus.id,
        categoryId: this.$route.params.categoryId,
        mobileCreatedAt: Date.now(),
        rateLimited: this.rateLimitingEnabled,
      };

      this.$store.dispatch('categoryStates/sendCategoryState', params);
    },
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-view-container {
    @include flashcard-view;
  }
</style>
